import { CRMAPIManager } from '@classes/crmApiManager';
import { messageService } from '@classes/messageService';
import { useMutation } from '@tanstack/react-query';

import { ExtractInnerData, MutateFnKey, ParamsMap, ResponseMap } from './types';

interface IProps<K extends MutateFnKey> {
    mutateFnKey: K;
    params?: ParamsMap[K];
    onSuccess?: (data: ExtractInnerData<ResponseMap[K]>) => void;
    onError?: (error: unknown) => void;
}

function isArrayParams<K extends MutateFnKey>(
    params: ParamsMap[K]
): params is ParamsMap[K] & unknown[] {
    return Array.isArray(params);
}

export const useMutationData = <K extends MutateFnKey>({
    mutateFnKey,
    params,
    onSuccess,
    onError,
}: IProps<K>) => {
    const {
        data,
        error,
        isError,
        isIdle,
        isPending,
        isPaused,
        isSuccess,
        failureCount,
        failureReason,
        mutate,
        mutateAsync,
        reset,
        status,
        submittedAt,
        variables,
    } = useMutation({
        mutationFn: async (variables: ParamsMap[K]) => {
            variables = variables ?? params;

            const result = await CRMAPIManager.request<ResponseMap[K]>((api) => {
                if (isArrayParams(variables)) {
                    // используется для variables: array
                    return api[mutateFnKey](...(variables ?? params));
                } else if (typeof params === 'object' && params !== null) {
                    // используется для variables: object
                    return (api[mutateFnKey] as (arg: ParamsMap[K]) => any)(variables);
                } else {
                    //используется для variables number|void
                    return (api[mutateFnKey] as (arg: ParamsMap[K]) => any)(variables);
                }
            });
            return result?.data?.data;
        },
        onError(error, variables, context) {
            messageService.sendError(error.message);
            onError?.(error);
        },
        onSuccess(data, variables, context) {
            messageService.sendSuccess('Данные изменены успешно');
            onSuccess?.(data);
        },
        retry: 0,
    });
    return {
        data,
        error,
        isError,
        isIdle,
        isPending,
        isPaused,
        isSuccess,
        failureCount,
        failureReason,
        mutate,
        mutateAsync,
        reset,
        status,
        submittedAt,
        variables,
    };
};
